import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { getUser, listUsers, listUsersByRole, updateUserData } from '../Api/User/UserApi';
import { addError } from './Error';
import { fetchUserCustomization, UserCustomizations } from './UserCustomizations';
import { initializePendo } from '../pendo';
import { fetchCommissionsInfo } from './GeneralStore';
import { setDataDogUser } from '../datadog';
import { fetchRPMRun } from './RpmRun';
import { fetchActiveRun } from './Run';

export interface UserEvent {
  id?: string;
  user: User;
  eventType: string;
  content: Record<string, unknown>;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  ENGINEER = 'ENGINEER',
  ANALYST = 'ANALYST',
  DATA = 'DATA',
  OBSERVER = 'OBSERVER',
  INACTIVE = 'INACTIVE'
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  lastLogin?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  events: UserEvent[];
  preferences: UserCustomizations;
}

const userSubject = new BehaviorSubject<User | undefined>(undefined);
const cruxUsersSubject = new BehaviorSubject<User[]>([] as User[]);
const userListSubject = new BehaviorSubject<User[]>([] as User[]);
const userLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxUsersLoadingSubject = new BehaviorSubject<boolean>(false);
const userDetailSubject = new BehaviorSubject<User | undefined>(undefined);

const clearError = () => addError('user', undefined);

export const getUserDetailSubject = (): BehaviorSubject<User | undefined> => userDetailSubject;
export const getUserSubject = (): BehaviorSubject<User | undefined> => userSubject;
export const getCruxUsersSubject = (): BehaviorSubject<User[]> => cruxUsersSubject;
export const getUsersByRolesSubject = (): BehaviorSubject<User[]> => userListSubject;

export const getCruxUsersLoadingSubject = (): BehaviorSubject<boolean> => cruxUsersLoadingSubject;

export const getUserLoadingSubject = (): BehaviorSubject<boolean> => userLoadingSubject;
export const getCurrentUser = (): User | undefined => userSubject.getValue();

export const isAdmin = () => getCurrentUser()?.role === UserRole.ADMIN;
export const isObserver = () => getCurrentUser()?.role === UserRole.OBSERVER;
export const isData = () => getCurrentUser()?.role === UserRole.DATA;
export const isAnalyst = () => getCurrentUser()?.role === UserRole.ANALYST;

const hasRolePermission = (allowedRoles: UserRole[]): boolean => {
  const role = getCurrentUser()?.role;
  return !!role && allowedRoles.includes(role);
};

const DATA_OR_HIGHER_ROLES = [UserRole.ADMIN, UserRole.ENGINEER, UserRole.ANALYST, UserRole.DATA];
const ANALYST_OR_HIGHER_ROLES = [UserRole.ADMIN, UserRole.ENGINEER, UserRole.ANALYST];
const ENGINEER_OR_HIGHER_ROLES = [UserRole.ADMIN, UserRole.ENGINEER];

export const hasDataOrHigherPermissions = () => hasRolePermission(DATA_OR_HIGHER_ROLES);
export const hasAnalystOrHigherPermissions = () => hasRolePermission(ANALYST_OR_HIGHER_ROLES);
export const hasEngineerOrHigherPermissions = () => hasRolePermission(ENGINEER_OR_HIGHER_ROLES);

export const updateUserDetail = (userData: User) => userDetailSubject.next(userData);

export const updateUser = async (userUpdate: User) => {
  const { data, error } = await updateUserData(userUpdate);
  if (data) {
    clearError();
    fetchUser(true);
  }
  if (error) addError('user', error.message);
};

export const fetchUsers = async () => {
  cruxUsersLoadingSubject.next(true);

  const { data, error } = await listUsers();

  if (data) {
    clearError();
    cruxUsersSubject.next(data);
  }
  if (error) addError('user', error.message);

  cruxUsersLoadingSubject.next(false);
};

export const fetchUser = async (forceRefresh = false) => {
  if (userLoadingSubject.getValue()) return;
  if (userSubject.getValue() && !forceRefresh) {
    await fetchCommissionsInfo();
    return;
  }
  userLoadingSubject.next(true);

  const { data, error } = await getUser();

  if (data) {
    clearError();
    if (process.env.NODE_ENV !== 'test') initializePendo({ ...data } as User);
    fetchActiveRun();
    fetchRPMRun();
    await fetchCommissionsInfo(forceRefresh);
    setDataDogUser(data as User);
    data.preferences = await fetchUserCustomization();
    userSubject.next(data);
  }
  if (error) addError('user', error.message);

  userLoadingSubject.next(false);
};

export const fetchUsersByRole = async (roles: UserRole[]) => {
  const { data, error } = await listUsersByRole(roles);

  if (data) {
    clearError();
    userListSubject.next(data);
  }
  if (error) addError('user', error.message);
};
